import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PhoneCall, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const inputCls = "w-full rounded-lg border border-gold/20 bg-navy-950/80 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/30";

export const CallbackModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", preferred_time: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/enquiries", {
        type: "callback",
        name: form.name,
        phone: form.phone,
        preferred_time: form.preferred_time,
        message: "Callback requested",
      });
      setStatus({ state: "success", msg: "" });
      setForm({ name: "", phone: "", preferred_time: "" });
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err) });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-end justify-center bg-navy-950/80 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-2xl border border-gold/25 bg-navy-900 p-7 shadow-2xl shadow-black/60 sm:rounded-2xl"
          >
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                  <PhoneCall size={19} className="text-gold" />
                </span>
                <div>
                  <h3 className="font-display text-xl font-bold text-slate-50">Request a Callback</h3>
                  <p className="text-xs text-slate-400">We typically call back within the hour.</p>
                </div>
              </div>
              <button data-testid="callback-modal-close-btn" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-navy-800 hover:text-gold">
                <X size={20} />
              </button>
            </div>

            {status.state === "success" ? (
              <div data-testid="callback-success-message" className="flex flex-col items-center py-8 text-center">
                <CheckCircle2 size={48} className="mb-4 text-gold" />
                <h4 className="font-display text-lg font-semibold text-slate-50">We'll Call You Back</h4>
                <p className="mt-2 text-sm text-slate-400">Your request has been received. Expect our call shortly.</p>
                <button onClick={onClose} className="mt-6 rounded-full border border-gold/40 px-6 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10">Close</button>
              </div>
            ) : (
              <form data-testid="callback-modal-form" onSubmit={submit} className="space-y-4">
                <input required data-testid="callback-form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Full name *" />
                <input required type="tel" data-testid="callback-form-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="Phone number *" />
                <input data-testid="callback-form-time-input" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className={inputCls} placeholder="Best time to call (e.g. Today after 3 PM)" />
                {status.state === "error" && (
                  <p data-testid="callback-form-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{status.msg}</p>
                )}
                <button
                  data-testid="callback-form-submit-btn"
                  type="submit"
                  disabled={status.state === "loading"}
                  className="btn-sheen w-full rounded-full bg-gradient-to-r from-[#D4AF37] via-[#C5A059] to-[#B38F32] py-3.5 text-sm font-bold text-navy-950 shadow-lg shadow-gold/25 transition-all hover:from-[#E6CA65] active:scale-[0.98] disabled:opacity-60"
                >
                  {status.state === "loading" ? "Sending..." : "Call Me Back"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
